import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, Gift, Play, GlobeHemisphereWest, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { unwrap, extractRows } from "../../lib/helpers";
import { DynamicTable } from "../../components/DataViews";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const countryKey = (r) => r?.country_key || r?.key || r?.code;
const countryLabel = (r) => r?.name || r?.country_name || countryKey(r);

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="border border-border rounded-md bg-card p-6 space-y-4" data-testid={testId}>
      <h3 className="flex items-center gap-2 text-base font-display font-bold tracking-tight">
        <Icon className="h-5 w-5 text-blue-800 dark:text-blue-400" weight="bold" /> {title}
      </h3>
      {children}
    </section>
  );
}

export function OpsTab({ businessId, projectId }) {
  const [days, setDays] = useState("30");
  const [refreshCycle, setRefreshCycle] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState([]);
  const [projectAbroad, setProjectAbroad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [rateCountry, setRateCountry] = useState("");
  const [rates, setRates] = useState({ marketing_rate_paise: "", utility_rate_paise: "", authentication_rate_paise: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/dashboard/abroad/catalog", { params: { active_only: true } }),
        api.get("/dashboard/abroad/project", {
          params: { wydely_business_id: businessId, wydely_project_id: projectId, enabled_only: true },
        }),
      ]);
      setCatalog(unwrap(c.data));
      setProjectAbroad(unwrap(p.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const grantFreePlan = async () => {
    const d = parseInt(days, 10);
    if (!d || d < 1 || d > 3650) return toast.error("Days must be between 1 and 3650");
    setBusy("grant");
    try {
      await api.post("/dashboard/free-plan/grant", {
        wydely_business_id: businessId,
        wydely_project_id: projectId,
        days: d,
        refresh_aisensy_billing_cycle: refreshCycle,
      });
      toast.success(`Free plan granted for ${d} days`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const resumeProvisioning = async () => {
    setBusy("resume");
    try {
      await api.post("/dashboard/aisensy-provisioning/resume", {
        wydelyBusinessId: businessId,
        wydelyProjectId: projectId,
      });
      toast.success("AiSensy provisioning resume triggered");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const enableCountries = async () => {
    if (!selected.length) return toast.error("Select at least one country");
    setBusy("enable");
    try {
      await api.post("/dashboard/abroad/enable", {
        wydely_business_id: businessId,
        wydely_project_id: projectId,
        country_keys: selected,
      });
      toast.success(`Enabled: ${selected.join(", ")}`);
      setSelected([]);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const disableCountry = async (key) => {
    setBusy(`disable-${key}`);
    try {
      await api.post("/dashboard/abroad/disable", {
        wydely_business_id: businessId,
        wydely_project_id: projectId,
        country_key: key,
      });
      toast.success(`Disabled ${key}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const saveRates = async () => {
    if (!rateCountry) return toast.error("Pick a country first");
    const payload = {
      wydely_business_id: businessId,
      wydely_project_id: projectId,
      country_key: rateCountry,
    };
    for (const [k, v] of Object.entries(rates)) {
      if (v !== "") payload[k] = parseInt(v, 10);
    }
    setBusy("rates");
    try {
      await api.put("/dashboard/abroad/rates", payload);
      toast.success(`Rates updated for ${rateCountry}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const catalogRows = extractRows(catalog);
  const enabledRows = extractRows(projectAbroad);
  const enabledKeys = new Set(enabledRows.map(countryKey).filter(Boolean));

  if (loading)
    return (
      <div className="space-y-3" data-testid="ops-loading">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    );

  return (
    <div className="space-y-6" data-testid="ops-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="ops-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Grant custom free plan" icon={Gift} testId="ops-grant-section">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="grant-days">Days (1–3650)</Label>
              <Input
                id="grant-days"
                type="number"
                min="1"
                max="3650"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                data-testid="grant-days-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={refreshCycle}
                onCheckedChange={(v) => setRefreshCycle(!!v)}
                data-testid="grant-refresh-cycle-checkbox"
              />
              Refresh AiSensy billing cycle
            </label>
            <Button onClick={grantFreePlan} disabled={busy === "grant"} data-testid="grant-submit-button">
              {busy === "grant" ? "Granting…" : "Grant free plan"}
            </Button>
          </div>
        </Section>

        <Section title="Resume AiSensy provisioning" icon={Play} testId="ops-resume-section">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Use after terminal AiSensy failures once ops confirms AiSensy is ready (including linking a replacement WABA).
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="resume-provisioning-trigger">Resume provisioning</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Resume AiSensy provisioning?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will retry provisioning for project <span className="font-data text-xs">{projectId}</span>. Only proceed if AiSensy side is confirmed ready.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="resume-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resumeProvisioning} disabled={busy === "resume"} data-testid="resume-confirm">
                  {busy === "resume" ? "Resuming…" : "Confirm resume"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>
      </div>

      <Section title="Enable abroad countries" icon={GlobeHemisphereWest} testId="ops-enable-countries-section">
        {catalogRows.length === 0 ? (
          <div className="text-sm text-muted-foreground">Catalog is empty.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-56 overflow-y-auto pr-2">
              {catalogRows.map((c, i) => {
                const key = countryKey(c);
                if (!key) return null;
                const checked = selected.includes(key);
                return (
                  <label
                    key={i}
                    data-testid={`catalog-country-${key}`}
                    className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${
                      enabledKeys.has(key)
                        ? "border-emerald-500/40 bg-emerald-500/5 text-muted-foreground"
                        : checked
                          ? "border-blue-800 bg-accent"
                          : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={enabledKeys.has(key)}
                      onCheckedChange={(v) =>
                        setSelected((s) => (v ? [...s, key] : s.filter((x) => x !== key)))
                      }
                      data-testid={`catalog-checkbox-${key}`}
                    />
                    <span className="truncate">{String(countryLabel(c))}</span>
                    {enabledKeys.has(key) && <span className="text-[9px] uppercase tracking-widest text-emerald-600 ml-auto">on</span>}
                  </label>
                );
              })}
            </div>
            <Button onClick={enableCountries} disabled={busy === "enable" || !selected.length} data-testid="enable-countries-button">
              {busy === "enable" ? "Enabling…" : `Enable selected (${selected.length})`}
            </Button>
          </>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Enabled countries (this project)" icon={GlobeHemisphereWest} testId="ops-enabled-countries-section">
          <DynamicTable
            rows={enabledRows}
            testId="ops-abroad-table"
            empty="No abroad countries enabled."
            maxCols={6}
            renderActions={(r) => {
              const key = countryKey(r);
              if (!key) return null;
              return (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:text-red-600"
                  disabled={busy === `disable-${key}`}
                  onClick={() => disableCountry(key)}
                  data-testid={`disable-country-${key}`}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              );
            }}
          />
        </Section>

        <Section title="Override country rates" icon={GlobeHemisphereWest} testId="ops-rates-section">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Country</Label>
              <Select value={rateCountry} onValueChange={setRateCountry}>
                <SelectTrigger data-testid="rate-country-select">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {[...enabledKeys].map((k) => (
                    <SelectItem key={k} value={k} data-testid={`rate-country-${k}`}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {[
              ["marketing_rate_paise", "Marketing rate (deci-paise)"],
              ["utility_rate_paise", "Utility rate (deci-paise)"],
              ["authentication_rate_paise", "Authentication rate (deci-paise)"],
            ].map(([k, label]) => (
              <div className="space-y-2" key={k}>
                <Label htmlFor={k}>{label}</Label>
                <Input
                  id={k}
                  type="number"
                  min="0"
                  value={rates[k]}
                  onChange={(e) => setRates((r) => ({ ...r, [k]: e.target.value }))}
                  data-testid={`rate-input-${k}`}
                />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Rates are deci-paise (₹1 = 1000). Blank fields are left unchanged.</p>
            <Button onClick={saveRates} disabled={busy === "rates" || !rateCountry} data-testid="save-rates-button">
              {busy === "rates" ? "Saving…" : "Save rates"}
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
